import React from 'react';
import { QrCode } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getProductFrameClass, DEFAULT_IMAGE_POSITION } from '../../utils/memorialPhotoLinkBuilder';
import { getImageEditStyle } from '../../utils/memorialPhotoEdits';

/**
 * Premium framed keepsake mockup for Preview Mode and Live Mode.
 */
export function KeepsakeFrameMockup({
  imageUrl,
  name,
  dates,
  memorialTitle,
  frameStyle,
  layout,
  product,
  orientation = 'portrait',
  imagePosition,
  imageEdits,
  published = false,
  className,
  size = 'md',
  showSafeArea = false,
}) {
  const pos = imagePosition || DEFAULT_IMAGE_POSITION;
  const aspectClass = getProductFrameClass(product, orientation);

  const sizeClasses = {
    sm: cn(aspectClass, 'w-28 max-w-full'),
    md: cn(aspectClass, 'w-36 max-w-full'),
    lg: cn(aspectClass, 'w-40 max-w-full sm:w-44'),
    fill: cn(aspectClass, 'w-full'),
  };

  const objectFit = pos.fitMode === 'contain' ? 'object-contain' : 'object-cover';
  const imageStyle = imageUrl ? getImageEditStyle(imageEdits, pos) : null;

  return (
    <div className={cn('flex flex-col items-center', className)} data-testid="keepsake-frame-mockup">
      <div
        className={cn(
          'rounded-lg p-2 shadow-[0_8px_28px_-12px_rgba(28,25,23,0.25)]',
          frameStyle?.ring || 'border-[#1c1917] bg-[#1c1917]',
          sizeClasses[size] || sizeClasses.md,
        )}
      >
        <div
          className={cn(
            'relative flex h-full w-full flex-col overflow-hidden rounded-sm border border-[hsl(38_14%_86%)]/40',
            frameStyle?.mat || 'bg-[#faf8f5]',
          )}
        >
          <div className="relative min-h-0 flex-1 overflow-hidden bg-[hsl(42_28%_96%)]">
            {showSafeArea ? (
              <div
                className="pointer-events-none absolute inset-[12%] z-10 rounded border border-dashed border-[#8b7355]/50"
                aria-hidden
              />
            ) : null}
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                draggable={false}
                className={cn('h-full w-full', objectFit)}
                style={imageStyle}
              />
            ) : (
              <div className="flex h-full min-h-[72px] items-center justify-center font-serif text-2xl text-[hsl(38_36%_42%)]/60">
                {name?.charAt(0) || '·'}
              </div>
            )}
          </div>
          {layout?.showNameDates ? (
            <div className="border-t border-[hsl(38_14%_88%)] bg-[hsl(42_28%_98%)] px-2 py-1.5 text-center">
              <p className="truncate font-serif text-[10px] font-medium text-[#1c1917]">{name}</p>
              {dates ? <p className="text-[9px] text-[#5c534a]">{dates}</p> : null}
              {layout?.showTributeLine && memorialTitle ? (
                <p className="mt-0.5 line-clamp-2 text-[8px] italic text-[#5c534a]/90">{memorialTitle}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {layout?.showQrNote ? (
        <p className="mt-2 flex items-center gap-1 text-[9px] text-[#5c534a]">
          <QrCode className="h-3 w-3" aria-hidden />
          QR on back · {published ? 'Live link' : 'Activation pending'}
        </p>
      ) : null}
    </div>
  );
}

export function QrActivationPlaceholder({ published, className }) {
  return (
    <div
      className={cn(
        'flex h-28 w-28 flex-col items-center justify-center rounded border border-dashed border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)] p-2 text-center',
        className,
      )}
      data-testid="qr-activation-placeholder"
    >
      <QrCode className="h-8 w-8 text-[#8b7355]/40" aria-hidden />
      <p className="mt-2 text-[10px] font-medium text-[#5c534a]">
        {published ? 'QR loading…' : 'Activation pending'}
      </p>
      {!published ? (
        <p className="mt-1 text-[9px] leading-tight text-muted-foreground">Live QR/NFC activates after publishing</p>
      ) : null}
    </div>
  );
}

export function ModeBadge({ published }) {
  if (published) {
    return (
      <span className="inline-flex items-center rounded-full border border-emerald-200/80 bg-emerald-50/80 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
        Live Mode
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-[hsl(43_48%_58%_/_0.35)] bg-[hsl(42_28%_98%)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">
      Preview Mode
    </span>
  );
}

export function OrientationToggle({ orientation, onChange, className }) {
  return (
    <div className={cn('inline-flex rounded-full border border-[hsl(38_14%_86%)] bg-white/80 p-0.5', className)} role="group" aria-label="Orientation">
      {['portrait', 'landscape'].map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={cn(
            'rounded-full px-3 py-1 text-[11px] font-medium capitalize transition',
            orientation === value
              ? 'bg-[hsl(152_32%_20%)] text-[hsl(42_30%_96%)]'
              : 'text-[#5c534a] hover:text-[#1c1917]',
          )}
        >
          {value}
        </button>
      ))}
    </div>
  );
}

export default KeepsakeFrameMockup;
